// src/components/ui/DataList/DataList.tsx
import { ReactNode } from 'react';
import { Flex } from '@/components/ui/Layout';
import {
  formatNumber,
  formatPercent,
  formatCurrency,
  formatSmartDate,
  truncateText
} from '@/utils/Formatters';
import styles from './DataList.module.css';

export interface DataListColumnDef<T> {
  key: string;
  title: string;
  width?: 'auto' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  textAlign?: 'left' | 'center' | 'right';
  render?: (item: T) => ReactNode;
  maxLength?: number; // Для обрезки длинного текста
  dataType?: 'text' | 'number' | 'currency' | 'percent' | 'date' | 'datetime'; // Новое свойство для форматирования
}

export interface DataListProps<T> {
  data: T[];
  columns: DataListColumnDef<T>[];
  className?: string;
  rowClassName?: string;
  noDataText?: string;
  compact?: boolean;
  colorKey?: string;
  getItemColor?: (item: T) => string;
}

export function DataList<T extends Record<string, any>>({
  data,
  columns,
  className = '',
  rowClassName = '',
  noDataText = 'Нет данных',
  compact = false,
  colorKey,
  getItemColor
}: DataListProps<T>) {
  // Функция для получения класса ширины колонки
  const getWidthClass = (width?: string) => {
    const widthType = width || 'auto';
    return styles[`width${widthType.charAt(0).toUpperCase() + widthType.slice(1)}`];
  };

  // Функция для получения класса выравнивания текста
  const getAlignClass = (align?: string) => {
    const alignType = align || 'left';
    return styles[`text${alignType.charAt(0).toUpperCase() + alignType.slice(1)}`];
  };

  // Определяем отступы на основе режима компактности
  const rowClass = compact ? styles.compactRow : styles.regularRow;

  // Функция для форматирования значений в зависимости от типа данных
  const formatValue = (value: any, dataType?: string, maxLength?: number): ReactNode => {
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    
    // Форматирование в зависимости от типа данных
    if (dataType === 'date') {
      return formatSmartDate(String(value), false);
    } else if (dataType === 'datetime') {
      return formatSmartDate(String(value), true);
    } else if (dataType === 'currency') {
      return formatCurrency(value);
    } else if (dataType === 'percent') {
      return formatPercent(value);
    } else if (dataType === 'number') {
      return formatNumber(value);
    }
    
    // Если тип данных не указан или это текст
    const textStr = String(value);
    
    // Обрезаем текст, если указана максимальная длина
    if (maxLength && textStr.length > maxLength) {
      return truncateText(textStr, maxLength);
    }
    
    return textStr;
  };

  if (data.length === 0) {
    return (
      <div className={styles.noData}>
        {noDataText}
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${className}`}>
      {/* Заголовок таблицы */}
      <div className={styles.header}>
        {columns.map((column) => (
          <div
            key={column.key}
            className={`${getWidthClass(column.width)} ${getAlignClass(column.textAlign)} ${styles.cell}`}
          >
            {column.title}
          </div>
        ))}
      </div>

      {/* Данные */}
      <div className={styles.dataContainer}>
        {data.map((item, rowIndex) => {
          // Получение цвета для строки, если задано
          const itemColor = colorKey 
            ? item[colorKey] 
            : getItemColor 
              ? getItemColor(item) 
              : undefined;

          return (
            <div
              key={rowIndex}
              className={`${styles.row} ${rowClass} ${rowClassName}`}
            >
              {columns.map((column, colIndex) => {
                // Получаем значение для ячейки с учетом типа данных
                const value = column.render 
                  ? column.render(item) 
                  : formatValue(item[column.key], column.dataType, column.maxLength);

                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={`${getWidthClass(column.width)} ${getAlignClass(column.textAlign)} ${styles.cell}`}
                  >
                    {/* Если это первая колонка и есть цвет, добавляем цветной маркер */}
                    {colIndex === 0 && itemColor ? (
                      <Flex align="center">
                        <div 
                          className={styles.colorMarker}
                          style={{ backgroundColor: itemColor }}
                        />
                        {value}
                      </Flex>
                    ) : (
                      value
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DataList;